import {useEffect, useRef, useState} from 'react'
import type {TimeValues} from './utils'
import {calculateTimeSaved} from './utils'

interface UseTimeSavedCounterParams {
  initialValues: TimeValues
  startingDate: string | null | undefined
  growthRate: number | null | undefined
}

/**
 * Custom hook to calculate and update time saved counter
 * Handles client-side calculation to show live values while preventing hydration mismatch
 * Continuously updates the counter based on the growth rate using requestAnimationFrame
 */
export const useTimeSavedCounter = ({initialValues, startingDate, growthRate}: UseTimeSavedCounterParams) => {
  const [isClient, setIsClient] = useState(false)
  const [values, setValues] = useState<TimeValues>(initialValues)

  // Use refs to store latest values without triggering effect re-runs
  const initialValuesRef = useRef(initialValues)
  const startingDateRef = useRef(startingDate)
  const growthRateRef = useRef(growthRate)
  const animationFrameRef = useRef<number | null>(null)
  const previousValuesRef = useRef<TimeValues>(initialValues)

  // Update refs when values change
  useEffect(() => {
    initialValuesRef.current = initialValues
    startingDateRef.current = startingDate
    growthRateRef.current = growthRate
  }, [initialValues, startingDate, growthRate])

  // Detect if running on client side to prevent hydration mismatch
  useEffect(() => {
    setIsClient(true)

    // Function to calculate and update values
    const updateValues = () => {
      // Only update if page is visible to save memory and CPU
      if (typeof document !== 'undefined' && document.hidden) {
        animationFrameRef.current = requestAnimationFrame(updateValues)
        return
      }

      if (startingDateRef.current && growthRateRef.current !== null && growthRateRef.current !== undefined) {
        const calculatedValues = calculateTimeSaved(
          initialValuesRef.current,
          startingDateRef.current,
          growthRateRef.current,
        )

        // Only update state if values have actually changed to prevent unnecessary re-renders
        const hasChanged =
          calculatedValues.years !== previousValuesRef.current.years ||
          calculatedValues.months !== previousValuesRef.current.months ||
          calculatedValues.days !== previousValuesRef.current.days ||
          calculatedValues.hours !== previousValuesRef.current.hours ||
          calculatedValues.minutes !== previousValuesRef.current.minutes

        if (hasChanged) {
          previousValuesRef.current = calculatedValues
          setValues(calculatedValues)
        }
      }

      // Schedule next update
      animationFrameRef.current = requestAnimationFrame(updateValues)
    }

    // Initial calculation and start animation loop
    updateValues()

    // Update immediately when tab becomes visible
    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        updateValues()
      }
    }

    // Listen for visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    // Cleanup animation frame and event listener on unmount
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
      }

      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [])

  return {values, isClient}
}
